/**
 * Admin ↔ Guard chat — threaded conversation viewer.
 *
 * Each guard owns one conversation row per society (created lazily on the
 * mobile side). Admins land here to triage and reply at scale. Left rail
 * lists every guard's conversation sorted by last_message_at; right pane
 * shows the selected thread + composer.
 *
 * Realtime: subscribe to the raw `conversation:message` socket event to
 * invalidate both the messages query (live-append) and the list query
 * (bump last_message_at + unread). 10s poll is the fallback.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Search, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { getSocket } from '@/lib/socket';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { nbhPalette, type NbhPaletteKey } from '@/components/ui/nbh';
import { cn } from '@/lib/utils';
import { formatRelative } from '@/lib/format';

interface ConversationItem {
  id: string;
  guard_id: string;
  guard: { id: string; full_name: string } | null;
  last_message_at: string | null;
  unread_for_admin: number;
  created_at: string;
}

interface Message {
  id: string;
  conversation_id: string;
  sender_role: 'guard' | 'admin' | string;
  body: string;
  created_at: string;
}

const PALETTE_KEYS: NbhPaletteKey[] = [
  'sky',
  'mint',
  'lavender',
  'peach',
  'yellow',
  'teal',
  'blush',
  'salmon'
];

function paletteFor(seed: string): NbhPaletteKey {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE_KEYS[h % PALETTE_KEYS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

function shortTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function ChatPage(): JSX.Element {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const threadRef = useRef<HTMLDivElement | null>(null);

  const convQ = useQuery({
    queryKey: ['conversations'],
    queryFn: () => get<{ items: ConversationItem[] }>('/conversations'),
    refetchInterval: 15_000
  });

  const conversations = convQ.data?.items ?? [];

  // Auto-select the first conversation once the list arrives.
  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      setSelectedId(conversations[0]!.id);
    }
  }, [conversations, selectedId]);

  const selected = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId]
  );

  const msgQ = useQuery({
    queryKey: ['conversations', selectedId, 'messages'],
    enabled: Boolean(selectedId),
    queryFn: async () => {
      const res = await get<{ items: Message[]; conversation: { id: string; guard_id: string } }>(
        `/conversations/${selectedId}/messages`
      );
      return res.items;
    },
    refetchInterval: 10_000
  });

  // Live-append: any `conversation:message` socket event invalidates the
  // currently-open thread + the list (so unread / last_message_at refresh).
  useEffect(() => {
    const socket = getSocket();
    const handler = (payload: { conversation_id: string }): void => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      if (payload.conversation_id === selectedId) {
        void qc.invalidateQueries({ queryKey: ['conversations', selectedId, 'messages'] });
      }
    };
    socket.on('conversation:message', handler);
    return () => {
      socket.off('conversation:message', handler);
    };
  }, [qc, selectedId]);

  // Scroll thread to bottom whenever new messages arrive.
  useEffect(() => {
    const node = threadRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  }, [msgQ.data?.length, selectedId]);

  const sendMut = useMutation({
    mutationFn: async (body: string) => {
      if (!selectedId) throw new Error('No conversation selected');
      return post<Message, { body: string }>(`/conversations/${selectedId}/messages`, { body });
    },
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['conversations', selectedId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  function handleSend(): void {
    const body = draft.trim();
    if (!body || sendMut.isPending) return;
    sendMut.mutate(body);
  }

  const filteredConversations = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter((c) =>
      (c.guard?.full_name ?? '').toLowerCase().includes(needle)
    );
  }, [conversations, q]);

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_for_admin ?? 0), 0);

  return (
    <PageHeader
      title="Admin chat"
      description={
        totalUnread > 0
          ? `${totalUnread} unread message${totalUnread === 1 ? '' : 's'} across ${conversations.length} guard${conversations.length === 1 ? '' : 's'}`
          : 'Reply to your guards in a single threaded view'
      }
    >
      <Card className="overflow-hidden rounded-vt-lg">
        <div className="grid h-[calc(100vh-220px)] min-h-[520px] grid-cols-1 md:grid-cols-[320px_1fr]">
          {/* ─── Left rail: conversations ────────────────────────── */}
          <aside className="flex min-h-0 flex-col border-r border-vt-border bg-vt-surface">
            <div className="border-b border-vt-border p-vt-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-vt-3 top-1/2 h-4 w-4 -translate-y-1/2 text-vt-text-soft"
                  aria-hidden="true"
                />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search guards…"
                  className="pl-9"
                  aria-label="Search guards"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {convQ.isLoading ? (
                <div className="space-y-vt-2 p-vt-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-vt-md" />
                  ))}
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-vt-6">
                  <EmptyState
                    icon={<MessageCircle className="h-6 w-6" strokeWidth={1.75} />}
                    title={q ? 'No matches' : 'No conversations yet'}
                    description={
                      q
                        ? 'Try a different name.'
                        : "Guards start a chat from the mobile app. You'll see threads here as they come in."
                    }
                  />
                </div>
              ) : (
                <ul className="divide-y divide-vt-border-soft">
                  {filteredConversations.map((c) => {
                    const name = c.guard?.full_name ?? 'Unknown guard';
                    const p = nbhPalette(paletteFor(c.guard_id));
                    const active = c.id === selectedId;
                    const unread = c.unread_for_admin > 0;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(c.id)}
                          className={cn(
                            'flex w-full items-center gap-vt-3 px-vt-3 py-vt-3 text-left transition-colors duration-vt',
                            active ? 'bg-vt-primary-100' : 'hover:bg-vt-surface-2'
                          )}
                          aria-current={active ? 'true' : undefined}
                        >
                          <span
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-vt-12 font-bold shadow-vt-1 ring-2 ring-white/60"
                            style={{ backgroundColor: p.fg, color: '#fff' }}
                            aria-hidden="true"
                          >
                            {initials(name)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-vt-2">
                              <div
                                className={cn(
                                  'truncate text-vt-14 tracking-[-0.01em] text-vt-text',
                                  unread ? 'font-bold' : 'font-semibold'
                                )}
                              >
                                {name}
                              </div>
                              {c.last_message_at ? (
                                <div className="shrink-0 text-vt-12 text-vt-text-soft">
                                  {formatRelative(c.last_message_at)}
                                </div>
                              ) : null}
                            </div>
                            <div className="mt-0.5 flex items-center justify-between gap-vt-2">
                              <div className="truncate text-vt-12 text-vt-text-muted">
                                {c.last_message_at ? 'Open thread' : 'No messages yet'}
                              </div>
                              {unread ? (
                                <span
                                  className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-vt-primary px-1.5 text-[11px] font-bold leading-none text-white"
                                  aria-label={`${c.unread_for_admin} unread`}
                                >
                                  {c.unread_for_admin > 9 ? '9+' : c.unread_for_admin}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* ─── Right pane: thread + composer ───────────────────── */}
          <section className="flex min-h-0 flex-col bg-vt-bg">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center p-vt-6">
                <EmptyState
                  icon={<MessageCircle className="h-6 w-6" strokeWidth={1.75} />}
                  title="Pick a conversation"
                  description="Select a guard on the left to start replying."
                />
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-vt-3 border-b border-vt-border bg-vt-surface px-vt-4 py-vt-3">
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-full text-vt-12 font-bold shadow-vt-1 ring-2 ring-white/60"
                    style={{ backgroundColor: nbhPalette(paletteFor(selected.guard_id)).fg, color: '#fff' }}
                    aria-hidden="true"
                  >
                    {initials(selected.guard?.full_name ?? 'G')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-vt-14 font-bold tracking-[-0.01em] text-vt-text">
                      {selected.guard?.full_name ?? 'Unknown guard'}
                    </div>
                    <div className="mt-0.5 inline-flex items-center gap-1 text-vt-12 text-vt-text-muted">
                      <ShieldCheck className="h-3 w-3" strokeWidth={2.25} aria-hidden="true" />
                      Guard · direct line
                    </div>
                  </div>
                </div>

                {/* Thread body */}
                <div
                  ref={threadRef}
                  className="flex-1 overflow-y-auto px-vt-4 py-vt-4"
                  aria-live="polite"
                  aria-busy={msgQ.isLoading}
                >
                  {msgQ.isLoading ? (
                    <div className="space-y-vt-2">
                      <Skeleton className="h-12 w-2/3 rounded-vt-md" />
                      <Skeleton className="ml-auto h-12 w-1/2 rounded-vt-md" />
                      <Skeleton className="h-12 w-3/5 rounded-vt-md" />
                    </div>
                  ) : (msgQ.data ?? []).length === 0 ? (
                    <EmptyState
                      icon={<MessageCircle className="h-6 w-6" strokeWidth={1.75} />}
                      title="No messages yet"
                      description="Say hi — your reply will reach the guard on their phone instantly."
                    />
                  ) : (
                    <div className="flex flex-col gap-vt-2">
                      {(msgQ.data ?? []).map((m, idx, arr) => {
                        const mine = m.sender_role === 'admin';
                        const prev = arr[idx - 1];
                        const newGroup = !prev || prev.sender_role !== m.sender_role;
                        return (
                          <div
                            key={m.id}
                            className={cn(
                              'flex max-w-[78%] flex-col gap-0.5',
                              mine ? 'ml-auto items-end' : 'mr-auto items-start',
                              newGroup ? 'mt-vt-2' : ''
                            )}
                          >
                            <div
                              className={cn(
                                'rounded-vt-md px-vt-3 py-vt-2 text-vt-14 leading-snug shadow-vt-1',
                                mine
                                  ? 'rounded-br-sm bg-vt-primary text-white'
                                  : 'rounded-bl-sm bg-vt-surface text-vt-text'
                              )}
                            >
                              <div className="whitespace-pre-wrap break-words">{m.body}</div>
                            </div>
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-vt-text-soft">
                              {shortTime(m.created_at)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-vt-border bg-vt-surface p-vt-3">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSend();
                    }}
                    className="flex items-end gap-vt-2"
                  >
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder="Type a reply… (Shift+Enter for new line)"
                      rows={1}
                      maxLength={4000}
                      disabled={sendMut.isPending}
                      className="min-h-[40px] max-h-[160px] flex-1 resize-none rounded-vt-md border border-vt-border bg-vt-bg px-vt-3 py-vt-2 text-vt-14 text-vt-text outline-none transition-colors focus-visible:border-vt-primary-400 focus-visible:ring-2 focus-visible:ring-vt-primary-400/40 disabled:opacity-60"
                      aria-label="Reply"
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!draft.trim() || sendMut.isPending}
                      aria-label="Send"
                      className="shrink-0"
                    >
                      <Send className="h-4 w-4" strokeWidth={2.25} />
                      <span className="ml-2 hidden sm:inline">{sendMut.isPending ? 'Sending…' : 'Send'}</span>
                    </Button>
                  </form>
                </div>
              </>
            )}
          </section>
        </div>
      </Card>
    </PageHeader>
  );
}
